import { Component, OnInit } from '@angular/core';
import { TranslateService } from "@ngx-translate/core";
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormGroup, Validators, ValidatorFn } from '@angular/forms';
import { Router } from '@angular/router';
import { ItemRequest, ItemNoSerialNumberRequest, PrepairedItemRequest, PrepairedItemNoSerialNumberRequest, ItemsService, ProductsService, CompanySettingsResponse } from '@src/app/shared/backend/clients';
import { firstValueFrom, Subject, of, timer } from 'rxjs';
import { catchError, map, switchMap, takeUntil, filter, take, distinctUntilChanged } from 'rxjs/operators';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { SessionService } from '@src/app/shared/services/session.service';
import { ItemDisposition } from '@src/app/shared/backend/clients/model/itemDisposition';
import { ComboData } from '@src/app/shared/components/comboData/comboData';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';

@Component({
    selector: 'app-add-item',
    templateUrl: './add-item.component.html',
    styleUrl: './add-item.component.scss',
    host: {
        class: 'app-add-item'
    },
    standalone: false
})
export class AddItemComponent extends BaseViewComponent implements OnInit {

  private readonly destroyedComponent$ = new Subject<void>();

  isPrepairedCompany: boolean = false;
  companyHasCpo: boolean = false;
  showSecurityCodeField: boolean = false;
  showSecurityCodeVersionField: boolean = false;

  private lastSelectedProductId: number | null = null;
  serialNumberRequired = false;

  companySettings: CompanySettingsResponse | undefined;
  companyId: number | undefined;

  currentDate: Date = new Date();

  comboData: ComboData = new ComboData(async (filter) => await firstValueFrom(this.productsService.apiProductsFilterComboPost(filter)));

  private serialNumberCheckCache = new Map<string, any>();

  public itemForm: FormGroup = this.fb.group({
    productId: [[], [Validators.required]],
    serialNumber: [{ value: '', disabled: true }, [Validators.maxLength(50)]],
    securityCode: [null],
    securityCodeVersion: [null],
    productionDate: [null],
    warrantyCode: [null, [Validators.maxLength(50)]],
    warrantyDurationMonths: [null],
    isCpo: ['false'],
    isGenuine: ['true'],
    implicitCodePairing: ['true']
  },
    { validators: this.conditionalValidators() });

  constructor(
    private router: Router,
    private translate: TranslateService,
    private session: SessionService,
    private itemsService: ItemsService,
    private productsService: ProductsService,
    private fb: FormBuilder) { super(); }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.session.currentCompanyData$
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(company => {
        this.showSecurityCodeField = company?.showSecurityCode ?? false;
        this.showSecurityCodeVersionField = company?.showSecurityCodeVersion ?? false;
        this.isPrepairedCompany = company?.isPrepaired ?? false;
        this.companyHasCpo = !!company?.hasCpo;
        this.cdr.detectChanges();
      });

    await this.initComboData();

    this.itemForm.get('productId')?.valueChanges
      .pipe(
        takeUntil(this.destroyedComponent$),
        map((value: any) => value?.[0] ?? null),
        distinctUntilChanged()
      )
      .subscribe((productId: number | null) => {
        void this.applySerialNumberRequirement(productId ?? undefined);
      });
  }

  private async initComboData() {
    await this.comboData.getData(null, 1);
  }

  async onLazyLoadComboData(event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));

    await this.comboData.getData(search, activePage);
  }

  getComboTotalPages(): number {
    const totalRecords = this.comboData?.totalRecords ?? 0;
    const pageSize = Number(this.comboData?.rowsPerPageOption) || 5;

    return Math.max(1, Math.ceil(totalRecords / pageSize));
  }

  qrCodeScanned(data: any) {
    const value = String(data ?? '').trim();
    this.itemForm.controls['serialNumber'].setValue(value, { emitEvent: true });
    this.itemForm.controls['serialNumber'].markAsDirty();
    this.itemForm.controls['serialNumber'].markAsTouched();
  }

  private serialNumberUniqueValidator(): AsyncValidatorFn {
    return (control: AbstractControl) => {
      if (control.disabled) return of(null);

      const productId = this.itemForm.get('productId')?.value?.[0] ?? null;
      const serial = String(control.value ?? '').trim();

      if (!serial) return of(null);
      if (!productId) return of({ productRequiredForSerial: true });

      const cacheKey = `${productId}|${serial}`;
      if (this.serialNumberCheckCache.has(cacheKey)) {
        return of(this.serialNumberCheckCache.get(cacheKey));
      }

      const payload = {
        pageNumber: 1,
        pageSize: 1,
        filters: [
          { propertyName: 'serialNumber', operator: FilterOperator.EQUALS, values: [serial] },
          { propertyName: 'productId', operator: FilterOperator.EQUALS, values: [`${productId}`] }
        ]
      };

      // Debounce + auto-cancel (swithcmap) on new keystroke
      return timer(600).pipe(
        switchMap(() => this.itemsService.apiItemsFilterPost(payload)),
        map((itemsPage: any) => {
          const item = (itemsPage.results || [])[0];
          if (!item) {
            this.serialNumberCheckCache.set(cacheKey, null);
            return null;
          }

          const err = item.disposition === ItemDisposition.Sold
            ? { serialNumberSold: true }
            : { serialNumberExists: true };

          this.serialNumberCheckCache.set(cacheKey, err);
          return err;
        }),
        catchError(() => of({ serialValidationFailed: true }))
      );
    };
  }

  private async applySerialNumberRequirement(productId?: number): Promise<void> {
    const serialNumberCtrl = this.itemForm.get('serialNumber');
    if (!serialNumberCtrl) return;

    this.serialNumberCheckCache.clear();
    serialNumberCtrl.reset(null, { emitEvent: false });
    serialNumberCtrl.markAsPristine();
    serialNumberCtrl.markAsUntouched();

    this.serialNumberRequired = false;
    serialNumberCtrl.clearValidators();
    serialNumberCtrl.clearAsyncValidators();
    serialNumberCtrl.setValidators([Validators.maxLength(50)]);
    serialNumberCtrl.disable({ emitEvent: false });
    serialNumberCtrl.updateValueAndValidity({ emitEvent: false });

    if (!productId) {
      this.lastSelectedProductId = null;
      return;
    }

    this.lastSelectedProductId = productId;

    try {
      const product = await firstValueFrom(this.productsService.apiProductsIdGet(productId));

      if (this.lastSelectedProductId !== productId) return;

      this.serialNumberRequired = !!product?.isSerialNumberRequired;
      
      serialNumberCtrl.enable({ emitEvent: false });
      serialNumberCtrl.setValidators([this.serialNumberRequired ? Validators.required : Validators.nullValidator,
                                      Validators.maxLength(50)]);
      serialNumberCtrl.setAsyncValidators([this.serialNumberUniqueValidator()]);
      serialNumberCtrl.updateValueAndValidity({ emitEvent: false });

    } catch (error) {
      if (this.lastSelectedProductId !== productId) return;

      console.error('Error fetching product', error);

      this.serialNumberRequired = true;
      serialNumberCtrl.enable({ emitEvent: false });
      serialNumberCtrl.setValidators([Validators.required, Validators.maxLength(50)]);
      serialNumberCtrl.setAsyncValidators([this.serialNumberUniqueValidator()]);
      serialNumberCtrl.updateValueAndValidity({ emitEvent: false });
    } finally {
      this.cdr.detectChanges();
    }
  }

  conditionalValidators(): ValidatorFn {
    return (formGroup: AbstractControl): { [key: string]: any } | null => {
      const implicitCodePairing = formGroup.get('implicitCodePairing')?.value;
      const warrantyCode = formGroup.get('warrantyCode')?.value;

      if (this.isPrepairedCompany && implicitCodePairing === 'false' && !warrantyCode) {
        return { requiredPrepaired: true };
      }

      return null;
    };
  }

  async submitForm(): Promise<void> {
    this.itemForm.markAllAsTouched();

    // check SN validation is running
    if (this.itemForm.pending) {
      await firstValueFrom(
        this.itemForm.statusChanges.pipe(
          filter(status => status !== 'PENDING'),
          take(1)
        )
      );
    }

    if (this.itemForm.invalid) return;

    const serialNumber = String(this.itemForm.controls['serialNumber'].value ?? '').trim();

    const basePayload = {
      productId: this.itemForm.controls['productId'].value?.[0],
      securityCode: this.itemForm.controls['securityCode'].value,
      securityCodeVersion: this.itemForm.controls['securityCodeVersion'].value,
      productionDate: this.itemForm.controls['productionDate'].value,
      warrantyDurationMonths: this.itemForm.controls['warrantyDurationMonths'].value,
      isCpo: this.itemForm.controls['isCpo'].value === 'true',
      isGenuine: this.itemForm.controls['isGenuine'].value === 'true'
    };

    if (this.isPrepairedCompany) {

      const warrantyCode = this.itemForm.controls['warrantyCode'].value;
      const implicitCodePairing = this.itemForm.controls['implicitCodePairing'].value === 'true';

      if (serialNumber != '') {
        const payload: PrepairedItemRequest = {
          ...basePayload,
          serialNumber,
          warrantyCode,
          implicitCodePairing
        };
        await firstValueFrom(this.itemsService.apiItemsCreatePrepairedPost(payload));
      } else {
        const payload: PrepairedItemNoSerialNumberRequest = {
          ...basePayload,
          warrantyCode,
          implicitCodePairing
        };
        await firstValueFrom(this.itemsService.apiItemsCreatePrepairedNoSnPost(payload));
      }
    } else {
      if (serialNumber != '') {
        const payload: ItemRequest = {
          ...basePayload,
          serialNumber
        };
        await firstValueFrom(this.itemsService.apiItemsCreatePost(payload));
      } else {
        const payload: ItemNoSerialNumberRequest = {
          ...basePayload
        };
        await firstValueFrom(this.itemsService.apiItemsCreateNoSnPost(payload));
      }
    }

    this.itemForm.controls['productId'].setValue([]);
    this.itemForm.controls['serialNumber'].setValue(null);
    this.itemForm.controls['securityCode'].setValue(null);
    this.itemForm.controls['securityCodeVersion'].setValue(null);
    this.itemForm.controls['warrantyCode'].setValue(null);
    this.itemForm.controls['warrantyDurationMonths'].setValue(null);
    this.itemForm.controls['productionDate'].setValue(null);
    this.itemForm.controls['isCpo'].setValue(null);
    this.itemForm.controls['isGenuine'].setValue(null);
    this.itemForm.controls['implicitCodePairing'].setValue('true');

    await this.applySerialNumberRequirement();

    this.router.navigate([`/items`]);
  }

  ngOnDestroy(): void {
    this.destroyedComponent$.next();
    this.destroyedComponent$.complete();
  }
}
